import { HTMLProps, PropsWithoutRef, forwardRef } from 'react';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';

import { fontStyles } from '@/styles/typography';

import { LiquidityProviderAvatar } from '@/ui/Avatars/LiquidityProviderAvatar';

import { classNames, twMerge } from '@/utils/classnames';
import { LiquiditySource, distinctSourcesProportionDescending } from '@/utils/liquidity-sources';

type Props = {
  liquiditySources: LiquiditySource[];
  loading?: boolean;
} & PropsWithoutRef<HTMLProps<HTMLButtonElement>>;

export const BestRouteButton = forwardRef<HTMLButtonElement, Omit<Props, 'children' | 'type'>>(
  function BestRouteButton({ className, liquiditySources, loading, ...rest }, forwardedRef) {
    const distinctSources = distinctSourcesProportionDescending(liquiditySources);

    if (loading) {
      return (
        <button
          className={twMerge('flex items-center gap-1.5 p-1.5', className)}
          ref={forwardedRef}
          {...rest}
          disabled={true}
          aria-busy={true}
        >
          <AvatarSkeleton size={20} radius={4} />
          <TextSkeleton width={80} height={16} />
        </button>
      );
    }

    // need to spread props over the button to ensure compatibility with Radix triggers
    return (
      <button
        className={twMerge(
          'flex gap-1.5 items-center p-1.5 pr-2 rounded-[10px] transition-colors hover:bg-token-purple-300 active:bg-token-purple-400 focus-visible:token-gray-600',
          className,
        )}
        {...rest}
        ref={forwardedRef}
      >
        <span className="flex relative">
          {distinctSources.slice(0, 3).map((source) => (
            <LiquidityProviderAvatar
              key={source}
              name={source}
              size={20}
              className={classNames(
                'transition-[margin-left] duration-200 ease-(--motion-ease-out) outline-none first:z-5 nth-[2]:ml-[-9px] nth-[2]:z-4 not-first:last:ml-[-9px]',
                '[button:hover_&]:nth-[2]:z-4 not-first:last:z-3 [button:hover_&]:nth-[2]:ml-[-3px]',
                '[button:hover_&]:not-first:last:ml-[-3px] [button:hover_&]:not-first:last:z-3',
              )}
            />
          ))}
        </span>
        <span
          className={fontStyles({
            variant: 'LABEL_MD',
            class: [
              'bg-token-purple-900',
              '[animation-iteration-count:1]',
              'before:hidden',
              'after:hidden',
              'shimmer-sweep',
              'highlight-[rgba(255,255,255,0.6)]',
              'motion-reduce:animate-none',
            ],
          })}
        >
          Best route
        </span>
      </button>
    );
  },
);
